import { useMemo } from 'react';

import { useJiraCustomFields } from '@/hooks/useJiraCustomFields';
import type {
  JiraCustomField,
  JiraCustomFieldDefault,
  JiraCustomFieldOption,
} from '@/types';

export interface JiraCustomFieldDefaultsProps {
  accountKey?: string;
  projectKey?: string;
  issueType?: string;
  /** Setup-time credentials (used when accountKey is not yet known). */
  config?: Record<string, unknown>;
  value: JiraCustomFieldDefault[];
  onChange: (value: JiraCustomFieldDefault[]) => void;
  disabled?: boolean;
}

// Shared styling for every editor input so the rows stay visually aligned.
const INPUT_CLASS =
  'bg-chariot-background-secondary border-chariot-border w-full rounded border px-2 py-1 text-sm';

// -----------------------------------------------------------------------------
// Shape helpers — keep type guards in one place so the per-type editors stay
// focused on rendering. Each helper is forgiving: anything it doesn't
// recognize collapses to an empty-ish value the input components can render.
// -----------------------------------------------------------------------------

/** Pulls the `value` out of a stored `{ value: string }` payload. */
const readOptionValue = (current: unknown): string => {
  if (
    typeof current === 'object' &&
    current !== null &&
    'value' in current &&
    typeof (current as { value?: unknown }).value === 'string'
  ) {
    return (current as { value: string }).value;
  }
  return '';
};

/** Pulls `{ parent, child }` out of a stored cascading payload. */
const readCascading = (current: unknown): { parent: string; child: string } => {
  const parent = readOptionValue(current);
  if (
    typeof current === 'object' &&
    current !== null &&
    'child' in current &&
    typeof (current as { child?: unknown }).child === 'object'
  ) {
    const child = readOptionValue((current as { child: unknown }).child);
    return { parent, child };
  }
  return { parent, child: '' };
};

/** Pulls the `accountId` out of a stored user payload. */
const readAccountId = (current: unknown): string => {
  if (
    typeof current === 'object' &&
    current !== null &&
    'accountId' in current &&
    typeof (current as { accountId?: unknown }).accountId === 'string'
  ) {
    return (current as { accountId: string }).accountId;
  }
  return '';
};

/** Pulls the selected values from a stored multi-select payload. */
const readMultiSelect = (current: unknown): Set<string> => {
  if (!Array.isArray(current)) return new Set();
  return new Set(
    (current as Array<{ value?: unknown }>)
      .map(o => (typeof o?.value === 'string' ? (o.value as string) : ''))
      .filter(Boolean)
  );
};

// -----------------------------------------------------------------------------
// Public component
// -----------------------------------------------------------------------------

/**
 * Editor for default values of Jira required custom fields. Mounted in the
 * SetupModal's configuration step beneath the auto-create / severities
 * controls — the framing is "values applied to every ticket auto-created at
 * the configured severity threshold".
 *
 * Only renders fields the backend identifies as required + custom; the four
 * built-in fields (project, issuetype, summary, description) are handled
 * elsewhere by createIssuePayload.
 */
export const JiraCustomFieldDefaults = ({
  accountKey,
  projectKey,
  issueType,
  config,
  value,
  onChange,
  disabled = false,
}: JiraCustomFieldDefaultsProps) => {
  const { data, isLoading, error } = useJiraCustomFields({
    accountKey,
    projectKey,
    issueType,
    config,
    enabled: !disabled && !!projectKey && !!issueType,
  });

  const fields = data?.fields ?? [];

  // Quick lookup keyed by field id so per-field handlers don't have to scan
  // the whole list on every keystroke.
  const valuesById = useMemo(() => {
    const map = new Map<string, JiraCustomFieldDefault>();
    for (const v of value) map.set(v.id, v);
    return map;
  }, [value]);

  const updateField = (field: JiraCustomField, nextValue: unknown) => {
    const next: JiraCustomFieldDefault[] = value.filter(v => v.id !== field.id);
    // Empty-ish values clear the entry rather than persist a "" / [] default
    // (avoids sending a payload that Jira will reject for the wrong reason).
    if (
      nextValue === undefined ||
      nextValue === '' ||
      (Array.isArray(nextValue) && nextValue.length === 0)
    ) {
      onChange(next);
      return;
    }
    next.push({ id: field.id, type: field.type, value: nextValue });
    onChange(next);
  };

  if (!projectKey || !issueType) return null;

  if (isLoading) {
    return (
      <div className="text-chariot-text-secondary text-sm">
        Loading required custom fields…
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-400">
        Failed to load custom fields. You can still save the template; tickets
        may fail to create until required fields are populated.
      </div>
    );
  }

  if (fields.length === 0) {
    return (
      <div className="text-chariot-text-secondary text-sm">
        No required custom fields configured for this project + issue type.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div>
        <p className="text-chariot-text-primary text-sm font-medium">
          Required custom field defaults
        </p>
        <p className="text-chariot-text-secondary text-xs">
          Applied to every ticket auto-created when a risk meets the severity
          threshold above.
        </p>
      </div>
      <div className="space-y-3">
        {fields.map(field => (
          <FieldRow
            key={field.id}
            field={field}
            current={valuesById.get(field.id)?.value}
            onChange={next => updateField(field, next)}
          />
        ))}
      </div>
    </div>
  );
};

// -----------------------------------------------------------------------------
// Per-field rendering
// -----------------------------------------------------------------------------

interface FieldRowProps {
  field: JiraCustomField;
  current: unknown;
  onChange: (next: unknown) => void;
}

const FieldRow = ({ field, current, onChange }: FieldRowProps) => (
  <div className="space-y-1">
    <label
      htmlFor={`cfd-${field.id}`}
      className="text-chariot-text-primary block text-xs font-medium"
    >
      {field.name}
      <span className="text-chariot-text-secondary ml-1 font-normal">
        ({field.id})
      </span>
    </label>
    <FieldInput field={field} current={current} onChange={onChange} />
  </div>
);

// HTML input `type` attr per normalized Jira field type. Absence means the
// field type isn't a plain scalar and needs a bespoke editor below.
const PRIMITIVE_INPUT_TYPE: Record<string, string> = {
  string: 'text',
  number: 'number',
  date: 'date',
  datetime: 'datetime-local',
};

const FieldInput = ({ field, current, onChange }: FieldRowProps) => {
  // Scalars share a single <input>; only the HTML type attribute differs.
  if (field.type in PRIMITIVE_INPUT_TYPE) {
    return (
      <PrimitiveInput
        field={field}
        current={current}
        onChange={onChange}
        inputType={PRIMITIVE_INPUT_TYPE[field.type]}
      />
    );
  }

  switch (field.type) {
    case 'option':
      return (
        <SingleSelect field={field} current={current} onChange={onChange} />
      );
    case 'array':
      return (
        <MultiSelect field={field} current={current} onChange={onChange} />
      );
    case 'cascading':
      return <Cascading field={field} current={current} onChange={onChange} />;
    case 'labels':
      return (
        <LabelsInput field={field} current={current} onChange={onChange} />
      );
    case 'user':
      return <UserInput field={field} current={current} onChange={onChange} />;
    default:
      // Fall back to a text input so the user can at least save *something*.
      return (
        <PrimitiveInput
          field={field}
          current={current}
          onChange={onChange}
          inputType="text"
        />
      );
  }
};

// -----------------------------------------------------------------------------
// Editor components
// -----------------------------------------------------------------------------

interface PrimitiveInputProps extends FieldRowProps {
  inputType: string;
}

const PrimitiveInput = ({
  field,
  current,
  onChange,
  inputType,
}: PrimitiveInputProps) => {
  const isNumber = inputType === 'number';
  const displayValue = isNumber
    ? typeof current === 'number'
      ? current
      : ''
    : typeof current === 'string'
      ? current
      : '';

  return (
    <input
      id={`cfd-${field.id}`}
      type={inputType}
      className={INPUT_CLASS}
      value={displayValue}
      onChange={e => {
        if (!isNumber) {
          onChange(e.target.value);
          return;
        }
        if (e.target.value === '') {
          onChange('');
          return;
        }
        const n = Number(e.target.value);
        onChange(Number.isNaN(n) ? '' : n);
      }}
    />
  );
};

const LabelsInput = ({ field, current, onChange }: FieldRowProps) => (
  <input
    id={`cfd-${field.id}`}
    type="text"
    placeholder="comma-separated"
    className={INPUT_CLASS}
    value={Array.isArray(current) ? (current as string[]).join(',') : ''}
    onChange={e => {
      const parts = e.target.value
        .split(',')
        .map(s => s.trim())
        .filter(Boolean);
      onChange(parts);
    }}
  />
);

const UserInput = ({ field, current, onChange }: FieldRowProps) => (
  // Stored as { accountId } — ENG-2855 v1 doesn't support a typeahead so this
  // only matters if a tenant ever exposes a required user-picker field.
  <input
    id={`cfd-${field.id}`}
    type="text"
    placeholder="Jira accountId"
    className={INPUT_CLASS}
    value={readAccountId(current)}
    onChange={e =>
      onChange(e.target.value ? { accountId: e.target.value } : '')
    }
  />
);

const SingleSelect = ({ field, current, onChange }: FieldRowProps) => {
  const options = field.allowedValues ?? [];
  const selected = readOptionValue(current);

  return (
    <select
      id={`cfd-${field.id}`}
      className={INPUT_CLASS}
      value={selected}
      onChange={e => onChange(e.target.value ? { value: e.target.value } : '')}
    >
      <option value="">— select —</option>
      {options.map(opt => (
        <option key={opt.id || opt.value} value={opt.value}>
          {opt.value}
        </option>
      ))}
    </select>
  );
};

const MultiSelect = ({ field, current, onChange }: FieldRowProps) => {
  const options = field.allowedValues ?? [];
  const selected = readMultiSelect(current);

  const toggle = (val: string) => {
    const next = new Set(selected);
    if (next.has(val)) next.delete(val);
    else next.add(val);
    onChange(Array.from(next).map(value => ({ value })));
  };

  return (
    <div className="space-y-1">
      {options.map(opt => (
        <label
          key={opt.id || opt.value}
          className="flex items-center gap-2 text-sm"
        >
          <input
            type="checkbox"
            checked={selected.has(opt.value)}
            onChange={() => toggle(opt.value)}
          />
          {opt.value}
        </label>
      ))}
    </div>
  );
};

const Cascading = ({ field, current, onChange }: FieldRowProps) => {
  const options = field.allowedValues ?? [];
  const { parent: parentValue, child: childValue } = readCascading(current);

  const parentOpt = options.find(o => o.value === parentValue);
  const childOptions: JiraCustomFieldOption[] = parentOpt?.child ?? [];

  return (
    <div className="flex gap-2">
      <select
        className={`${INPUT_CLASS} flex-1`}
        value={parentValue}
        onChange={e => {
          const v = e.target.value;
          onChange(v ? { value: v } : '');
        }}
      >
        <option value="">— select parent —</option>
        {options.map(opt => (
          <option key={opt.id || opt.value} value={opt.value}>
            {opt.value}
          </option>
        ))}
      </select>
      <select
        className={`${INPUT_CLASS} flex-1`}
        value={childValue}
        disabled={!parentValue || childOptions.length === 0}
        onChange={e => {
          const v = e.target.value;
          if (!parentValue) return;
          onChange(
            v
              ? { value: parentValue, child: { value: v } }
              : { value: parentValue }
          );
        }}
      >
        <option value="">— select child —</option>
        {childOptions.map(opt => (
          <option key={opt.id || opt.value} value={opt.value}>
            {opt.value}
          </option>
        ))}
      </select>
    </div>
  );
};
